/* 芯片迷你图用的轻量工具。3D 大图渲染由 src/crystal/CrystalViewer.tsx
   （移植自 system0）负责，判键用它的 calculateBonds。 */

import { getElementColor as _sysColor } from './crystal/elementColors';

export function elementColor(el, idx = 0) {
  try {
    const c = _sysColor(el, 'jmol');
    if (c && c !== 'Cc') return c;
  } catch (e) { /* fallthrough */ }
  const FALLBACK = ['#ab7df8', '#f0883e', '#4c8dff', '#3fb96f', '#e5534b'];
  return FALLBACK[idx % FALLBACK.length];
}

/** "MgB2" → [{el:'Mg',n:1},{el:'B',n:2}] */
export function parseFormula(f) {
  const out = [];
  const re = /([A-Z][a-z]?)(\d*\.?\d*)/g;
  let m;
  while (f && (m = re.exec(f))) {
    if (!m[1]) break;
    out.push({el: m[1], n: m[2] ? parseFloat(m[2]) : 1});
  }
  return out.length ? out : [{el: 'X', n: 1}];
}

/** 化学式 → 原子元素列表（总数封顶 cap） */
export function formulaToAtoms(formula, cap = 12) {
  const atoms = [];
  for (const {el, n} of parseFormula(formula)) {
    for (let i = 0; i < Math.round(n); i++) {
      if (atoms.length >= cap) return atoms;
      atoms.push(el);
    }
  }
  return atoms.length ? atoms : ['X'];
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}
function mulberry32(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 按化学式生成"像晶体局部"的假结构（演示模式专用），
 * 返回 {species, positions}，与后端 structure endpoint 同形状。
 */
export function genStructure(formula) {
  const els = formulaToAtoms(formula, 10);
  const rng = mulberry32(hashCode(formula || 'X'));
  const positions = [];
  els.forEach((el, i) => {
    if (i === 0) { positions.push([0, 0, 0]); return; }
    const theta = rng() * Math.PI * 2, phi = Math.acos(2 * rng() - 1);
    const r = 1.6 + rng() * 0.9;
    positions.push([
      r * Math.sin(phi) * Math.cos(theta),
      r * Math.sin(phi) * Math.sin(theta),
      r * Math.cos(phi),
    ]);
  });
  return {species: els, positions};
}

/** 后端/假结构 {species, positions, lattice} → CrystalViewer 的 ParsedStructure */
export function toParsedStructure(d) {
  if (!d || !d.species || !d.positions) return null;
  return {
    atoms: d.species.map((el, i) => ({
      element: el, label: el,
      x: d.positions[i][0], y: d.positions[i][1], z: d.positions[i][2],
    })),
    lattice: d.lattice && d.lattice.length === 9 ? d.lattice : null,
  };
}

/** 迷你图：3D → 2D 正交投影（按 z 从远到近排序，保留原始索引 i） */
export function miniProject(species, positions, rotX = 0.5, rotY = 0.7) {
  const cx = Math.cos(rotX), sx = Math.sin(rotX);
  const cy = Math.cos(rotY), sy = Math.sin(rotY);
  return (species || []).map((el, i) => {
    const [x, y, z] = positions[i] || [0, 0, 0];
    const x1 = x * cy + z * sy, z1 = -x * sy + z * cy;
    const y2 = y * cx - z1 * sx, z2 = y * sx + z1 * cx;
    return {el, i, x: x1, y: y2, z: z2};
  }).sort((a, b) => a.z - b.z);
}

/** 化学式 → 带下标的 JSX 片段 */
export function FormulaText({f}) {
  if (!f) return null;
  const parts = [];
  const re = /(\d+\.?\d*)|([A-Za-z][a-z]?)/g;
  let m, key = 0;
  while ((m = re.exec(f))) {
    if (m[1]) parts.push(<sub key={key++}>{m[1]}</sub>);
    else parts.push(<span key={key++}>{m[2]}</span>);
  }
  return <>{parts}</>;
}
